<template>
  <div class="offer-analytics-details">
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>

    <div class="offers-analytics-header">
      <div class="custom-card-header">
        <span>{{ $t('Analytics') }}</span>
      </div>

      <div class="search-row">
        <DxTextBox
          v-model="searchText"
          :placeholder="$t('search')"
          mode="search"
          styling-mode="outlined"
          width="100%"
          value-change-event="input"
        />
      </div>
    </div>

    <div v-if="filteredAnalyticsRows.length">
      <div class="table-header offers-analytics-table-header">
        <div></div>
        <div>{{ $t('article') }}</div>
        <div>{{ $t('description') }}</div>
        <div>{{ $t('agronomySample.summary.sampleType') }}</div>
        <div>{{ $t('netAmount') }}</div>
      </div>

      <div
        v-for="(analysis, index) in filteredAnalyticsRows"
        :key="analysisKey(analysis)"
      >
        <div
          class="table-row offers-analysis-row"
          @click="toggleAnalysis(index, analysis)"
        >
          <div class="expand-cell">
            <span class="expand-symbol">
              {{ expandedAnalysisIndex === index ? '-' : '+' }}
            </span>
          </div>
          <div>{{ analysis.Articulo }}</div>
          <div>{{ analysis.DescripcionAT }}</div>
          <div>{{ analysis.subFamiliaIdioma }}</div>
          <div>{{ calculateNetAmount(analysis) }}</div>
        </div>

        <div
          v-if="expandedAnalysisIndex === index"
          class="tableContainerSamples offers-analytics-child-container"
        >
          <div class="card-header-offers">
            {{ analysis.Articulo }}
          </div>

          <div
            v-if="isLoadingAnalysis(analysis)"
            class="detail-loading"
          >
            {{ $t('loading') }}
          </div>

          <template v-else-if="getAnalysisDetails(analysis).length">
            <div
              class="table-header-sample offers-analytics-child-header"
            >
              <div>{{ $t('foodThechnicalInfo.parameter') }}</div>
              <div>{{ $t('Subcontracted') }}</div>
              <div>{{ $t('Technique') }}</div>
              <div>{{ $t('PNT') }}</div>
              <div>{{ $t('MinimumValue') }}</div>
              <div>{{ $t('MaximumValue') }}</div>
              <div>{{ $t('UnitMeasure') }}</div>
              <div>{{ $t('AccreditationInfo') }}</div>
            </div>

            <div
              v-for="(
                parameter, parameterIndex
              ) in getAnalysisDetails(analysis)"
              :key="parameterKey(parameter, parameterIndex)"
              class="table-row-sample offers-analytics-child-row"
            >
              <div>{{ parameter.Parametro }}</div>
              <div>
                {{ formatSubcontracted(parameter.PTSubcontratado) }}
              </div>
              <div>{{ parameter.Tecnica }}</div>
              <div>{{ parameter.NombrePNT }}</div>
              <div>{{ parameter.ValorMinimo }}</div>
              <div>{{ parameter.ValorMaximo }}</div>
              <div>{{ parameter.UnidadMedida }}</div>
              <div>{{ parameter.Acreditacion }}</div>
            </div>
          </template>

          <div v-else class="offers-analytics-empty">
            {{ $t('noDataText') }}
          </div>
        </div>
      </div>
    </div>

    <div v-else class="offers-analytics-empty">
      {{ $t('noDataText') }}
    </div>
  </div>
</template>

<script>
import { DxTextBox } from 'devextreme-vue/text-box';
import { mapActions, mapGetters } from 'vuex';
import helpers from '@/js/helpers';
import { NOTIFY_TIME_LONG } from '@/js/constants';

export default {
  components: {
    DxTextBox,
  },
  props: {
    store: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      expandedAnalysisIndex: null,
      loadingAnalysisKeys: [],
      detailsByAnalysisKey: {},
      searchText: '',
      loaded: true,
    };
  },
  computed: {
    ...mapGetters('user', ['getLanguage']),
    ...mapGetters('contact', ['contactInfo']),
    normalizedQuery() {
      return (this.searchText || '').trim().toLowerCase();
    },
    filteredAnalyticsRows() {
      const rows = Array.isArray(this.store) ? this.store : [];
      const q = this.normalizedQuery;
      if (!q) return rows;

      return rows.filter((analysis, index) => {
        const analysisText = [
          analysis.Articulo,
          analysis.DescripcionAT,
          analysis.subFamiliaIdioma,
          analysis.PrecioNetoCodDivisa,
          this.calculateNetAmount(analysis),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (analysisText.includes(q)) return true;

        const key = this.analysisKey(analysis);
        const details = this.detailsByAnalysisKey[key] || [];
        return details.some((parameter) =>
          this.parameterMatchesQuery(parameter, q)
        );
      });
    },
  },
  watch: {
    searchText() {
      const current =
        this.filteredAnalyticsRows[this.expandedAnalysisIndex];
      if (!current) {
        this.expandedAnalysisIndex = null;
      }
    },
  },
  methods: {
    async toggleAnalysis(index, analysis) {
      if (this.expandedAnalysisIndex === index) {
        this.expandedAnalysisIndex = null;
        return;
      }

      this.expandedAnalysisIndex = index;
      await this.ensureAnalysisDetails(analysis);
    },
    async ensureAnalysisDetails(analysis) {
      const key = this.analysisKey(analysis);
      if (
        this.detailsByAnalysisKey[key] ||
        this.isLoadingAnalysis(analysis)
      ) {
        return;
      }

      this.loadingAnalysisKeys = [...this.loadingAnalysisKeys, key];

      try {
        const response = await this.fetchDetallesLineas({
          cod_articuloAT: analysis.CodArticulo,
          cod_planta: analysis.CodPlantaCentroProduccionSeleccionada,
          idTipoMuestra: analysis.idTipoMuestra,
        });

        this.$set(this.detailsByAnalysisKey, key, response || []);
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                this.getErrorMessage(error)
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
        this.$set(this.detailsByAnalysisKey, key, []);
      } finally {
        this.loadingAnalysisKeys = this.loadingAnalysisKeys.filter(
          (loadingKey) => loadingKey !== key
        );
      }
    },
    analysisKey(analysis) {
      return [
        analysis.CodArticulo,
        analysis.CodPlantaCentroProduccionSeleccionada,
        analysis.idTipoMuestra,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join('-');
    },
    parameterKey(parameter, fallback = '') {
      return [
        parameter.Parametro,
        parameter.Tecnica,
        parameter.NombrePNT,
        fallback,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join('-');
    },
    getAnalysisDetails(analysis) {
      const key = this.analysisKey(analysis);
      return this.detailsByAnalysisKey[key] || [];
    },
    isLoadingAnalysis(analysis) {
      const key = this.analysisKey(analysis);
      return this.loadingAnalysisKeys.includes(key);
    },
    getErrorMessage(error) {
      try {
        return JSON.parse(error.message).error;
      } catch {
        return error.message || error;
      }
    },
    parameterMatchesQuery(parameter, query) {
      return [
        parameter.Parametro,
        parameter.Tecnica,
        parameter.NombrePNT,
        parameter.ValorMinimo,
        parameter.ValorMaximo,
        parameter.UnidadMedida,
        parameter.Acreditacion,
        this.formatSubcontracted(parameter.PTSubcontratado),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    },
    formatSubcontracted(value) {
      return value ? this.$t('yes') : this.$t('no');
    },
    formatAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') {
        return '';
      }

      const parsedAmount = Number(
        typeof amount === 'string'
          ? amount.replace(',', '.').replace(/[^\d.-]/g, '')
          : amount
      );

      const locale = this.getLanguage || 'es';
      return helpers.formatCurrency(parsedAmount, currency, locale);
    },
    calculateNetAmount(row) {
      const rawValue = row.PrecioNetoCodDivisa || '';
      const [precioOriginal, moneda] = rawValue.split(' ');
      const precioBruto = Number(
        row.PrecioBruto || precioOriginal || 0
      );
      const dto = Number(row.Descuento || 0);
      let neto = precioBruto;
      if (dto !== 0) {
        neto = precioBruto - (precioBruto * dto) / 100;
      }
      return this.formatAmount(
        neto,
        this.getRowCurrency(row, moneda)
      );
    },
    getRowCurrency(row, moneda) {
      return helpers.resolveCurrency({
        conversionX3: row.ConversionX3,
        clientCurrency: this.contactInfo?.currency,
        additionalCurrencies: [row.CodDivisa],
        m: row.M,
        moneda,
        fallback: 'EUR',
      });
    },
    normalizeCurrency(currency) {
      return helpers.normalizeMonetaryUnit(currency);
    },
    ...mapActions('Offers', ['fetchDetallesLineas']),
  },
};
</script>

<style scoped lang="scss">
.offer-analytics-details {
  padding: 0 10px 10px;
}

.offers-analytics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 0 8px;
}

.offers-analytics-header .custom-card-header {
  margin: 0;
  padding: 0 !important;
}

.card-header-offers {
  color: #00346a !important;
  font-size: 17px !important;
  font-style: normal !important;
  font-weight: 500 !important;
  line-height: 120% !important;
  padding: 0 12px 12px !important;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 35px 1.1fr 2fr 1.3fr 1fr;
  padding: 12px;
  background: #f2f8e2;
  border-radius: 10px;
  align-items: center;
  color: #3e3c44;
  margin: 4px 0;
  gap: 8px;
}

.table-header {
  font-weight: 600;
  background-color: #f7f7f8;
}

.table-row {
  border-top: 1px solid #eee;
  color: #00346a;
  cursor: pointer;
  transition: background-color 0.2s;
}

.table-row:hover {
  background-color: #ecf7cc;
}

.expand-cell {
  font-weight: 600;
  text-align: center;
}

.expand-symbol {
  color: #00346a;
  cursor: pointer;
  display: inline-block;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  user-select: none;
}

.tableContainerSamples {
  padding: 20px;
  background-color: #fcfdf3;
  border-radius: 10px;
}

.table-header-sample,
.table-row-sample {
  display: grid;
  grid-template-columns: 1.6fr 0.8fr 1fr 1fr 0.8fr 0.8fr 1fr 1fr;
  padding: 10px 10px 10px 24px;
  border-radius: 10px;
  background: #ebf2fa;
  align-items: center;
  color: #3e3c44;
  margin: 4px 0;
  gap: 8px;
}

.table-header-sample {
  font-weight: 600;
  background-color: #efeff1;
}

.detail-loading,
.offers-analytics-empty {
  color: #8a8693;
  padding: 16px 12px;
  text-align: center;
}

.search-row {
  width: 200px;
  margin-left: auto;
}

.search-row :deep(.dx-texteditor) {
  width: 200px;
  height: 35px;
  min-height: 35px;
  border-radius: 999px;
}

.search-row :deep(.dx-texteditor-input) {
  font-size: 13px;
  padding-top: 6px;
  padding-bottom: 6px;
}

.search-row :deep(.dx-texteditor-buttons-container) {
  height: 32px;
}

.search-row :deep(.dx-icon) {
  font-size: 16px;
}

.search-row
  :deep(.dx-texteditor.dx-editor-outlined .dx-texteditor-input) {
  padding-left: 30px !important;
}

.search-row :deep(.dx-texteditor.dx-editor-outlined .dx-placeholder) {
  padding-left: 5px !important;
}
</style>
